import { useState, useEffect, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../app/auth/AuthProvider';
import { Logo } from './Logo';
import {
  Menu,
  X,
  ChevronDown,
  FileText,
  Receipt,
  Building2,
  Users,
  Package,
  Calculator,
  BarChart3,
  FileSpreadsheet,
  PieChart,
  Send,
  Settings,
  Smartphone,
  Puzzle,
  Briefcase,
  Store,
  UserCircle,
} from 'lucide-react';

interface SubMenuItem {
  label: string;
  description: string;
  href: string;
  icon: typeof FileText;
}

interface MenuCategory {
  title: string;
  items: SubMenuItem[];
}

interface NavItem {
  label: string;
  href: string;
  megaMenu?: {
    categories: MenuCategory[];
    cta?: {
      label: string;
      href: string;
    };
  };
}

const navItems: NavItem[] = [
  {
    label: 'Funktionen',
    href: '/funktionen',
    megaMenu: {
      categories: [
        {
          title: 'Buchhaltung',
          items: [
            { label: 'Rechnungen', description: 'Mit Nummernkreis, Vorlagen und E-Rechnung', href: '/funktionen/rechnungen', icon: FileText },
            { label: 'Aufträge', description: 'Angebote schreiben und in Rechnungen umwandeln', href: '/funktionen/auftraege', icon: Receipt },
            { label: 'Belegerfassung', description: 'Alle Belege an einem Ort sammeln', href: '/funktionen/belegerfassung', icon: Receipt },
            { label: 'Banking', description: 'Zahlungseingänge automatisch verbuchen', href: '/funktionen/banking', icon: Building2 },
            { label: 'Kontaktverwaltung', description: 'Alle Infos zu Kunden und Lieferanten', href: '/funktionen/kontakte', icon: Users },
            { label: 'Produkte & Dienstleistungen', description: 'Stundensätze und Bestände pflegen', href: '/funktionen/produkte', icon: Package },
          ],
        },
        {
          title: 'Steuern & Auswertung',
          items: [
            { label: 'Übersicht', description: 'Finanzen und Steuerlast überblicken', href: '/funktionen/uebersicht', icon: PieChart },
            { label: 'Umsatzsteuervoranmeldung', description: 'Vorausgefüllt und direkt ans Finanzamt', href: '/funktionen/umsatzsteuer', icon: Calculator },
            { label: 'EÜR & GuV', description: 'Gewinnermittlung aus Belegen und Rechnungen', href: '/funktionen/euer', icon: FileSpreadsheet },
            { label: 'BWA', description: 'Deinen Unternehmenserfolg bewerten', href: '/funktionen/bwa', icon: BarChart3 },
            { label: 'Summen- und Saldenliste', description: 'Buchungssätze detailgenau nachvollziehen', href: '/funktionen/saldenliste', icon: FileSpreadsheet },
            { label: 'Exporte', description: 'DATEV- und ADDISON-konform', href: '/funktionen/exporte', icon: Send },
          ],
        },
        {
          title: 'Erweiterungen',
          items: [
            { label: 'Integrationen', description: 'Verbindungen zu anderen Tools', href: '/funktionen/integrationen', icon: Puzzle },
            { label: 'Mobile App', description: 'Buchhaltung von unterwegs', href: '/funktionen/mobile-app', icon: Smartphone },
          ],
        },
      ],
      cta: {
        label: 'Alle Funktionen ansehen',
        href: '/funktionen',
      },
    },
  },
  {
    label: 'Lösungen',
    href: '/loesungen',
    megaMenu: {
      categories: [
        {
          title: 'Nach Unternehmensgröße',
          items: [
            { label: 'Freelancer', description: 'Einfache Buchhaltung für Selbstständige', href: '/loesungen/freelancer', icon: UserCircle },
            { label: 'Kleinunternehmer', description: 'Optimiert für Kleinunternehmerregelung', href: '/loesungen/kleinunternehmer', icon: Store },
            { label: 'Startups', description: 'Skalierbare Lösung für Gründer', href: '/loesungen/startups', icon: Briefcase },
            { label: 'KMU', description: 'Professionelle Tools für Mittelständler', href: '/loesungen/kmu', icon: Building2 },
          ],
        },
        {
          title: 'Nach Branche',
          items: [
            { label: 'Dienstleister', description: 'Zeiterfassung und Projektabrechnung', href: '/loesungen/dienstleister', icon: Briefcase },
            { label: 'E-Commerce', description: 'Anbindung an Shopsysteme', href: '/loesungen/ecommerce', icon: Store },
            { label: 'Handwerk', description: 'Angebote, Aufträge und Rechnungen', href: '/loesungen/handwerk', icon: Settings },
          ],
        },
      ],
    },
  },
  { label: 'Preise', href: '/preise' },
  { label: 'Für Steuerberater', href: '/steuerberater' },
];

export default function Header(): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuEnter = (label: string) => {
    setActiveMenu(label);
  };

  const handleMenuLeave = () => {
    setActiveMenu(null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || activeMenu
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group text-secondary-900" aria-label="Saldify Startseite">
            <Logo className="h-9 w-auto lg:h-10 transition-opacity group-hover:opacity-90" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.megaMenu && handleMenuEnter(item.label)}
                onMouseLeave={handleMenuLeave}
              >
                {item.megaMenu ? (
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors hover:text-primary-600 rounded-lg hover:bg-primary-50 ${
                      activeMenu === item.label ? 'text-primary-600 bg-primary-50' : 'text-secondary-700'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === item.label ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className="px-4 py-2 text-sm font-medium text-secondary-700 transition-colors hover:text-primary-600 rounded-lg hover:bg-primary-50"
                  >
                    {item.label}
                  </Link>
                )}

                {/* Mega Menu Dropdown */}
                {item.megaMenu && activeMenu === item.label && (
                  <div className="absolute top-full left-0 pt-2" onMouseEnter={() => handleMenuEnter(item.label)}>
                    <div className="bg-white rounded-2xl shadow-2xl border border-secondary-100 p-6 min-w-[700px] animate-slide-down">
                      <div className="grid grid-cols-3 gap-8">
                        {item.megaMenu.categories.map((category) => (
                          <div key={category.title}>
                            <h3 className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-4">
                              {category.title}
                            </h3>
                            <ul className="space-y-1">
                              {category.items.map((subItem) => (
                                <li key={subItem.label}>
                                  <Link
                                    to={subItem.href}
                                    className="flex items-start gap-3 p-2 rounded-lg hover:bg-secondary-50 transition-colors group"
                                    onClick={() => setActiveMenu(null)}
                                  >
                                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                                      <subItem.icon className="w-4 h-4 text-primary-600" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-secondary-900 group-hover:text-primary-600 transition-colors">
                                        {subItem.label}
                                      </div>
                                      <div className="text-xs text-secondary-500">
                                        {subItem.description}
                                      </div>
                                    </div>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {item.megaMenu.cta && (
                        <div className="mt-6 pt-4 border-t border-secondary-100">
                          <Link
                            to={item.megaMenu.cta.href}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            onClick={() => setActiveMenu(null)}
                          >
                            {item.megaMenu.cta.label}
                            <span className="text-lg">→</span>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <Link
                to="/app"
                className="btn-primary px-5 py-2.5 text-sm shadow-lg shadow-primary-600/30 hover:shadow-primary-600/50 transition-all"
              >
                Zur App
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-secondary-700 transition-colors hover:text-primary-600"
                >
                  Einloggen
                </Link>
                <Link
                  to="/register"
                  className="btn-primary px-5 py-2.5 text-sm shadow-lg shadow-primary-600/30 hover:shadow-primary-600/50 transition-all"
                >
                  Kostenlos testen
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-secondary-100 transition-colors"
            aria-label={isMenuOpen ? 'Menu schließen' : 'Menu öffnen'}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-secondary-700" />
            ) : (
              <Menu className="w-6 h-6 text-secondary-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMenuOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white border-t border-secondary-100 px-4 py-4 space-y-2">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.megaMenu ? (
                <MobileMenuSection item={item} onClose={() => setIsMenuOpen(false)} />
              ) : (
                <Link
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-secondary-700 hover:bg-secondary-50 hover:text-primary-600 rounded-lg transition-colors font-medium"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="pt-4 border-t border-secondary-100 space-y-2">
            {user ? (
              <Link
                to="/app"
                onClick={() => setIsMenuOpen(false)}
                className="block btn-primary text-center py-3"
              >
                Zur App
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 text-secondary-700 hover:bg-secondary-50 hover:text-primary-600 rounded-lg transition-colors"
                >
                  Einloggen
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className="block btn-primary text-center py-3"
                >
                  Kostenlos testen
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// Mobile menu section component
function MobileMenuSection({ item, onClose }: { item: NavItem; onClose: () => void }): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-3 text-secondary-700 hover:bg-secondary-50 rounded-lg transition-colors font-medium"
      >
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && item.megaMenu && (
        <div className="pl-4 pb-2 space-y-4">
          {item.megaMenu.categories.map((category) => (
            <div key={category.title}>
              <h4 className="px-4 py-2 text-xs font-semibold text-primary-600 uppercase tracking-wider">
                {category.title}
              </h4>
              {category.items.map((subItem) => (
                <Link
                  key={subItem.label}
                  to={subItem.href}
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-secondary-600 hover:bg-secondary-50 hover:text-primary-600 rounded-lg transition-colors"
                >
                  <subItem.icon className="w-4 h-4" />
                  {subItem.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
